//_requests.ts

import axios, {AxiosRequestConfig} from 'axios'
import type {ContractBufferExceptionEvidence} from '../../Crewing/core/_models'
import {appendCrewOwnershipToFormData} from '../../Crewing/core/crewOwnership'
import {
  Crew,
  CrewDocument,
  CrewDocumentRequest,
  CrewDocumentResponse,
  CrewCertification,
  Vessel,
  AcademicQualificationRequest,
  AcademicQualificationResponse,
  WatchkeepingCertificateRequest,
  WatchkeepingCertificateResponse,
  NextOfKinRequest,
  NextOfKinResponse,
  BankingDetailsRequest,
  BankingDetailsResponse,
  CourseCertificate,
  CourseCertificateResponse,
  SeaServiceRequest,
  SeaServiceResponse,
  AdditionalDetailsResponse,
  AdditionalDetailsRequest,
  CrewUnit,
  ApprovalStatus,
} from './_models'
import {Voyage} from '../../operations/core/_models'
import {Rank} from './_models'
import {Company, CompanyAdmin, HistoryRecord} from './_models'
import {CrewFlagDocument, FlagCountry, FlagDocType} from './_models'
import {
  RollingExceptionResponse,
  RollingMatrixResponse,
  RollingPreviewResponse,
  PreviewSlotPayload,
} from './_models'

const API_URL = process.env.REACT_APP_API_URL
const CREW_API_URL = `${API_URL}/crew`
const SIGNEDON_CREW_API_URL = `${API_URL}/sign/active`
const RANKS_API = `${API_URL}/ranks`
const COMPANY_ADMIN_API_URL = `${API_URL}/company-group-admins`
const GET_COMPANY_API_URL = `${API_URL}/users/company-admins`
const VESSEL_API_URL = `${process.env.REACT_APP_API_URL}/vessels` // Assuming this is the endpoint for vessels
const REST_HOURS_API_URL = `${process.env.REACT_APP_API_URL}/rest-hours`
const REST_SUMMARY_RANGE_API = `${process.env.REACT_APP_API_URL}/rest-hours/summaries/range`

export const getCrewList = async (): Promise<Crew[]> => {
  try {
    const response = await axios.get(CREW_API_URL + '/all')
    return response.data
  } catch (error: any) {
    console.error('Error fetching crew list:', error)
    throw new Error(error.response?.data?.message || 'Error fetching Crew list')
  }
}

const formatLocalDate = (date: Date) => date.toISOString().split('T')[0] // "YYYY-MM-DD"
const formatLocalDateTime = (date: Date) => date.toISOString().split('.')[0]

function fmtDateTime(dt: Date) {
  // "2025-07-06T18:30:00"
  return dt.toISOString().split('.')[0]
}

export const getCrewUnit = async (crewId: number): Promise<CrewUnit> => {
  try {
    const response = await axios.get(`${API_URL}/crew/${crewId}`)
    return response.data
  } catch (error) {
    console.error('Error fetching crew unit:', error)
    throw new Error('Failed to fetch crew unit')
  }
}

export const createCrew = async (
  name: string,
  dateOfBirth: Date,
  nationality: string,
  department: string,
  contactNumber: string,
  email: string,
  username: string,
  password: string,
  rankId: number,
  companyGroupAdminId: number,
  companyAdminId: number | null,
  certifications: {documentName: string; file: File | null}[],
  documents: {documentName: string; file: File | null}[],
  presentRankId: number,
  rankAppliedForId: number | null,
  telNo: string,
  familyName: string,
  alternateMobNo: string,
  skypeId: string,
  maritalStatus: string,
  bloodGroup: string,
  heightCms: number,
  weightKgs: number,
  bmiIndex: number,
  willingToAcceptLowerRank: boolean,
  boilerSuitSize: string,
  shoeSize: string,
  address: string,
  placeOfBirth: string,
  dateOfAvailability: Date,
  dateOfJoining?: Date | null,
  notes?: string,
  employeeId?: string
): Promise<Crew> => {
  rankId = rankAppliedForId ?? rankId // if rankAppliedForId is given, use it as initial rankId
  const formData = new FormData()

  // —––– Primitive fields map 1:1 to your CrewDto properties —–––
  formData.append('name', name)
  formData.append('username', username)
  formData.append('nationality', nationality)
  formData.append('department', department)
  formData.append('contactNumber', contactNumber)
  formData.append('email', email)
  formData.append('password', password)
  formData.append('rankId', rankId.toString())
  appendCrewOwnershipToFormData(formData, {
    companyGroupAdminId,
    companyAdminId: companyAdminId != null && companyAdminId > 0 ? companyAdminId : null,
  })
  formData.append('presentRankId', presentRankId.toString())
  formData.append('dateOfBirth', formatLocalDate(dateOfBirth))
  formData.append('familyName', familyName)
  formData.append('placeOfBirth', placeOfBirth)
  formData.append('dateOfAvailability', formatLocalDate(dateOfAvailability))
  if (dateOfJoining) {
    formData.append('dateOfJoining', formatLocalDate(dateOfJoining))
  }
  formData.append('maritalStatus', maritalStatus)
  formData.append('bloodGroup', bloodGroup)
  formData.append('heightCms', heightCms.toString())
  formData.append('weightKgs', weightKgs.toString())
  formData.append('bmiIndex', bmiIndex.toString())
  formData.append('willingToAcceptLowerRank', String(willingToAcceptLowerRank))
  formData.append('address', address)
  formData.append('isActive', 'true') // Default for creation

  // Optional fields
  if (employeeId) formData.append('employeeId', employeeId)
  if (rankAppliedForId != null) {
    formData.append('rankAppliedForId', rankAppliedForId.toString())
  }
  if (telNo) formData.append('telNo', telNo)
  if (alternateMobNo) formData.append('alternateMobNo', alternateMobNo)
  if (skypeId) formData.append('skypeId', skypeId)
  if (boilerSuitSize) formData.append('boilerSuitSize', boilerSuitSize)
  if (shoeSize) formData.append('shoeSize', shoeSize)
  if (notes) formData.append('notes', notes)
  // if (isApproved != null) formData.append('isApproved', String(isApproved));

  // Certifications and Documents
  certifications.forEach((cert, idx) => {
    formData.append('certificationNames', cert.documentName)
    if (cert.file) {
      formData.append('certificationFiles', cert.file)
    }
  })

  documents.forEach((doc, idx) => {
    formData.append('documentNames', doc.documentName)
    if (doc.file) {
      formData.append('documentFiles', doc.file)
    }
  })

  try {
    const response = await axios.post(`${CREW_API_URL}/create`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })

    const data = response.data
    if (data?.id) return data as Crew
    throw new Error('Create Crew API did not return a valid Crew object')
  } catch (error: any) {
    console.error('Error creating crew:', error)
    throw new Error(error.response?.data?.message || 'Failed to create crew')
  }
}

export const updateCrew = async (
  crewId: number,
  crewDto: {
    name: string
    dateOfBirth: string
    nationality: string
    department: string
    contactNumber: string
    email: string
    rankId: number
    presentRankId?: number
    rankAppliedForId?: number
    companyGroupAdminId: number
    companyAdminId?: number | null
    active: boolean
    notes?: string
    familyName?: string
    placeOfBirth?: string
    gender?: string
    telNo?: string
    alternateMobNo?: string
    skypeId?: string
    dateOfAvailability?: string
    dateOfJoining?: string
    maritalStatus?: string
    bloodGroup?: string
    heightCms?: number
    weightKgs?: number
    bmiIndex?: number
    willingToAcceptLowerRank?: boolean
    boilerSuitSize?: string
    shoeSize?: string
    address?: string
    employeeId?: string
    // isApproved?: boolean;
  }
): Promise<Crew> => {
  try {
    crewDto.rankId = crewDto.rankAppliedForId ?? crewDto.rankId // if rankAppliedForId is given, use it as initial rankId
    const form = new FormData()
    form.append('name', crewDto.name)
    form.append('dateOfBirth', crewDto.dateOfBirth)
    form.append('nationality', crewDto.nationality)
    form.append('department', crewDto.department)
    form.append('contactNumber', crewDto.contactNumber)
    form.append('email', crewDto.email)
    form.append('rankId', crewDto.rankId.toString())
    form.append('companyGroupAdminId', crewDto.companyGroupAdminId.toString())
    form.append('active', String(crewDto.active))

    // Optional fields
    if (crewDto.employeeId) {
      form.append('employeeId', crewDto.employeeId)
    }
    if (crewDto.presentRankId != null) {
      form.append('presentRankId', crewDto.presentRankId.toString())
    }
    if (crewDto.rankAppliedForId != null) {
      form.append('rankAppliedForId', crewDto.rankAppliedForId.toString())
    }
    if (crewDto.companyAdminId != null) {
      form.append('companyAdminId', crewDto.companyAdminId.toString())
    }
    if (crewDto.notes) form.append('notes', crewDto.notes)
    if (crewDto.familyName) form.append('familyName', crewDto.familyName)
    if (crewDto.placeOfBirth) form.append('placeOfBirth', crewDto.placeOfBirth)
    if (crewDto.gender) form.append('gender', crewDto.gender)
    if (crewDto.telNo) form.append('telNo', crewDto.telNo)
    if (crewDto.alternateMobNo) form.append('alternateMobNo', crewDto.alternateMobNo)
    if (crewDto.skypeId) form.append('skypeId', crewDto.skypeId)
    if (crewDto.dateOfAvailability) form.append('dateOfAvailability', crewDto.dateOfAvailability)
    if (crewDto.dateOfJoining) form.append('dateOfJoining', crewDto.dateOfJoining)
    if (crewDto.maritalStatus) form.append('maritalStatus', crewDto.maritalStatus)
    if (crewDto.bloodGroup) form.append('bloodGroup', crewDto.bloodGroup)
    if (crewDto.heightCms != null) form.append('heightCms', crewDto.heightCms.toString())
    if (crewDto.weightKgs != null) form.append('weightKgs', crewDto.weightKgs.toString())
    if (crewDto.bmiIndex != null) form.append('bmiIndex', crewDto.bmiIndex.toString())
    if (crewDto.willingToAcceptLowerRank != null) {
      form.append('willingToAcceptLowerRank', String(crewDto.willingToAcceptLowerRank))
    }
    if (crewDto.boilerSuitSize) form.append('boilerSuitSize', crewDto.boilerSuitSize)
    if (crewDto.shoeSize) form.append('shoeSize', crewDto.shoeSize)
    if (crewDto.address) form.append('address', crewDto.address)
    if (crewDto.employeeId) form.append('employeeId', crewDto.employeeId)
    // if (crewDto.isApproved != null) form.append('isApproved', String(crewDto.isApproved));

    const response = await axios.put(`${CREW_API_URL}/update/${crewId}`, form, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })

    return response.data as Crew
  } catch (error: any) {
    console.error('Error updating crew:', error)
    throw new Error(error.response?.data?.message || 'Error updating crew')
  }
}

// New API to update the active status of a crew member
export const updateCrewStatus = async (crewId: number, active: boolean): Promise<Crew> => {
  try {
    const response = await axios.put(`${CREW_API_URL}/status-update/${crewId}`, {active})
    return response.data
  } catch (error: any) {
    console.error('Error updating crew status:', error)
    throw new Error(error.response?.data?.message || 'Error updating crew status')
  }
}

export const updateCrewApprovalStatus = async (
  crewId: number,
  status: ApprovalStatus
): Promise<Crew> => {
  const res = await axios.put(`${CREW_API_URL}/${crewId}/approval`, null, {
    params: {status}, // sends ?status=PENDING|APPROVED|REJECTED
  })
  return res.data as Crew
}

export const deleteVessel = async (crewId: number): Promise<Crew[]> => {
  try {
    const response = await axios.delete(CREW_API_URL + '/delete/' + crewId)

    return response.data
  } catch (error: any) {
    console.error('Error deleting crew item:', error)
    throw new Error(error.response?.data?.message || 'Error deleting crew item')
  }
}

// —–––– Vessels APIs ––––

export const getVesselList = async (): Promise<Vessel[]> => {
  try {
    const response = await axios.get(VESSEL_API_URL, {
      headers: {},
    })

    return response.data
  } catch (error: any) {
    console.error('Error fetching vessel list:', error)
    throw new Error(error.response?.data?.message || 'Error fetching vessel list')
  }
}

// —–––– Add Crew Documents APIs ––––
export const getDocumentsFormData = (crewId: number): Promise<CrewDocumentResponse> => {
  return axios.get(`${CREW_API_URL}/${crewId}/documents`).then((response) => response.data)
}

// —–––– Documents APIs ––––

// list documents with slot
export const getCrewDocuments = async (crewId: number): Promise<CrewDocument[]> => {
  const token = getOnboardingToken()
  if (token) {
    const res = await axios.get(`${API_URL}/crew/onboard/documents`, {
      params: {token},
    })
    return (res.data as any[]).map((doc) => ({
      id: doc.id,
      slot: doc.slot,
      documentName: doc.documentName ?? doc.fileName ?? doc.slot,
      filePath: doc.filePath ?? null,
      uploadedAt: doc.uploadedAt,
    }))
  }

  const res = await axios.get(`${API_URL}/documents/${crewId}`)
  return (res.data as any[]).map((doc) => ({
    id: doc.id,
    slot: doc.slot, // ← include
    documentName: doc.documentName ?? doc.fileName ?? doc.slot,
    filePath: doc.filePath ?? null,
    uploadedAt: doc.uploadedAt,
  }))
}

// Fetch a single document as a Blob (authorized)
export const fetchCrewDocumentBlob = async (crewId: number, docId: number): Promise<Blob> => {
  const token = getOnboardingToken()
  if (token) {
    const res = await axios.get(`${API_URL}/crew/onboard/documents/${docId}/view`, {
      params: {token},
      responseType: 'blob',
    })
    return res.data as Blob
  }
  const res = await axios.get(`${API_URL}/documents/${crewId}/${docId}/view`, {
    responseType: 'blob',
  })
  return res.data as Blob
}

// upload bound to a slot (upsert on server)
export const uploadCrewDocument = async (
  crewId: number,
  slot: string,
  file: File
): Promise<CrewDocument> => {
  const token = getOnboardingToken()
  if (token) {
    const form = new FormData()
    form.append('file', file)
    form.append('slot', slot)
    const res = await axios.post(`${API_URL}/crew/onboard/documents/upload`, form, {
      params: {token},
      headers: {'Content-Type': 'multipart/form-data'},
    })
    return {
      id: res.data.id,
      slot: res.data.slot,
      documentName: res.data.documentName ?? res.data.fileName ?? res.data.slot,
      filePath: res.data.filePath,
      uploadedAt: res.data.uploadedAt,
    }
  }

  const form = new FormData()
  form.append('file', file)
  form.append('slot', slot) // ← important

  const res = await axios.post(`${API_URL}/documents/${crewId}`, form, {
    headers: {'Content-Type': 'multipart/form-data'},
  })

  return {
    id: res.data.id,
    slot: res.data.slot,
    documentName: res.data.documentName ?? res.data.fileName ?? res.data.slot,
    filePath: res.data.filePath,
    uploadedAt: res.data.uploadedAt,
  }
}

export const deleteCrewDocument = async (crewId: number, docId: number): Promise<void> => {
  const token = getOnboardingToken()
  if (token) {
    await axios.delete(`${API_URL}/crew/onboard/documents/${docId}`, {
      params: {token},
    })
    return
  }
  await axios.delete(`${API_URL}/documents/${crewId}/${docId}`)
}

// —–––– Certifications APIs ––––

export const getCrewCertifications = async (crewId: number): Promise<CrewCertification[]> => {
  const res = await axios.get(`${API_URL}/certifications/${crewId}`)
  return (res.data as any[]).map((cert) => ({
    id: cert.id,
    documentName: cert.certificationName,
    // point at the new `/view` endpoint
    fileUrl: `${API_URL}/certifications/${crewId}/${cert.id}/view`,
  }))
}

export const uploadCrewCertification = async (
  crewId: number,
  documentName: string,
  file: File
): Promise<CrewCertification[]> => {
  const form = new FormData()
  form.append('certificationNames', documentName)
  // <-- same here:
  form.append('file', file)

  const res = await axios.post(`${API_URL}/certifications/${crewId}`, form, {
    headers: {'Content-Type': 'multipart/form-data'},
  })
  return res.data
}

export const deleteCrewCertification = async (crewId: number, certId: number): Promise<void> => {
  await axios.delete(`${API_URL}/certifications/${crewId}/${certId}`)
}

/**
 * Fetch all ranks so we can look up rankName by rankId.
 */
export const getRanks = async (): Promise<Rank[]> => {
  const res = await axios.get<Rank[]>(RANKS_API + '/drop-down')
  return res.data
}

export const getRanksforList = async (): Promise<Rank[]> => {
  const res = await axios.get<Rank[]>(RANKS_API)
  return res.data
}

/**
 * (optional) Fetch a single rank by id.
 */
export const getRankById = async (id: number): Promise<Rank> => {
  const res = await axios.get<Rank>(`${RANKS_API}/${id}`)
  return res.data
}

export interface SignOnRequest {
  contractBufferException?: boolean
  contractBufferExceptionReason?: string
  vesselId: number
  crewId: number
  // legacy contract fields (kept for compatibility with existing UI)
  contractStartDate: string // "YYYY-MM-DD"
  contractEndDate: string // "YYYY-MM-DD"
  // preferred explicit fields for backend DTO
  signOnDate?: string
  dateOfAgreement?: string
  signOnTime?: string
  relieverId?: string // Optional reliever ID
}

export interface SignOnResponse extends ContractBufferExceptionEvidence {
  id: number
  vesselId: number
  vesselName: string
  crewId: number
  crewName: string
  rank: number
  signOnDate: string
  signOnTime?: string | null
  signOffDate: string | null
  signOffReason: string | null
  portSignOn: string
  portSignOff: string | null
  travelArrangements: string | null
  status: string // "SIGNED_ON" etc
}

/**
 * Sign a crew on to a vessel.
 */
export const signOnCrew = async (payload: SignOnRequest): Promise<SignOnResponse> => {
  console.log('payload: ', payload)
  const res = await axios.post<SignOnResponse>(`${API_URL}/sign/on`, payload)
  return res.data
}

// at the bottom of core/_requests.ts

export interface SignOnOffRecord extends ContractBufferExceptionEvidence {
  id: number
  vesselId: number
  vesselName: string
  crewId: number
  crewName: string
  rank: number // this is the rankId
  signOnDate: string // "YYYY-MM-DD"
  dateOfAgreement?: string | null
  signOnTime?: string | null
  signOffDate: string | null
  signOffReason: string | null
  portSignOn: string
  portSignOff: string | null
  travelArrangements: string | null
  status: 'SIGNED_ON' | 'SIGNED_OFF' | string
  contractStartDate?: string | null // "YYYY-MM-DD"
  contractEndDate?: string | null // "YYYY-MM-DD"
  relieverName?: string | null // Name of the selected reliever
  relieverRank?: number | null // Rank ID of the selected reliever
}

/**
 * Fetch all sign-on / sign-off records.
 */
export const getSignOnOffRecords = async (): Promise<SignOnOffRecord[]> => {
  const res = await axios.get<SignOnOffRecord[]>(`${API_URL}/sign/on-off-records/all`)
  console.log('Signonof recs: ', res.data)
  return res.data
}

// at bottom of core/_requests.ts

export interface SignOffRequest {
  recordId: number
  signOffDate: string // "YYYY-MM-DD"
  signOffReason: string
  portSignOff: string
}

export interface SignOffResponse extends SignOnOffRecord {}

export const signOffCrew = async (payload: SignOffRequest): Promise<SignOffResponse> => {
  const res = await axios.post<SignOffResponse>(`${API_URL}/sign/off`, payload)
  return res.data
}

export const getCompanyList = async (): Promise<Company[]> => {
  try {
    const response = await axios.get(GET_COMPANY_API_URL)

    return response.data
  } catch (error: any) {
    console.error('Error fetching company list:', error)
    throw new Error(error.response?.data?.message || 'Error fetching company list')
  }
}

export const getCompanyAdminList = async (): Promise<CompanyAdmin[]> => {
  try {
    const response = await axios.get(COMPANY_ADMIN_API_URL)

    return response.data // Return company data
  } catch (error: any) {
    console.error('Error fetching company admin list:', error)
    throw new Error(error.response?.data?.message || 'Error fetching company admin list')
  }
}

// at the bottom, after signOffCrew...
export interface UpdateAssignmentPayload {
  contractBufferException?: boolean
  contractBufferExceptionReason?: string
  signOnDate?: string
  dateOfAgreement?: string
  contractEndDate?: string
  signOnTime?: string
  portSignOn: string
  signOffDate?: string
}

/**
 * Update a sign-on/off record (only PLANNED or SIGNED_ON allowed).
 */
export const updateAssignment = async (
  recordId: number,
  payload: UpdateAssignmentPayload
): Promise<SignOnOffRecord> => {
  const res = await axios.put<SignOnOffRecord>(`${API_URL}/sign/update/${recordId}`, payload)
  return res.data
}

/**
 * Fetch change‑history for one sign‑on/off record.
 */
export const getHistory = async (recordId: number): Promise<HistoryRecord[]> => {
  const res = await axios.get<HistoryRecord | HistoryRecord[]>(
    `${API_URL}/sign-on-records/${recordId}/history`
  )
  const data = res.data
  // normalize to array
  return Array.isArray(data) ? data : [data]
}

// Rest hours

// --- KEEP/REUSE your existing auth header helper if you have one ---
const jsonHeaders = {'Content-Type': 'application/json'}

// narrow string type for safety
export type Regulation = 'stcw' | 'mlc'
export type WorkType = 'NORMAL' | 'DRILL' | 'EMERGENCY' | 'OVERRIDING_OPERATIONAL' | 'CALL_OUT'
export type CompRestStatus = 'OPEN' | 'SATISFIED' | 'WAIVED'

export interface CompRestRequirement {
  id: number
  crewId: number
  vesselId: number
  reasonType: string
  eventStartTs: string
  eventEndTs: string
  requiredMinutes: number
  dueByTs: string
  status: CompRestStatus
  notes?: string | null
  satisfiedMinutes?: number | null
  satisfiedAtTs?: string | null
}

export interface RestHourViolation {
  description?: string
  ruleCode?: string
  standard?: string
}

export interface RestHourDailySummary {
  crewId: number
  vesselId: number
  summaryDate: string
  totalWorkHours: number
  totalRestHours: number
  compliant?: boolean
  isCompliant?: boolean
  violationCount?: number
  violations?: RestHourViolation[]
  exceptions?: {description?: string}[]
  remarks?: string | null
}

export interface RestHourEntry {
  slotIndex: number
}

// entriesByDate = { "2025-09-01": [{slotIndex: 0}, {slotIndex: 1}], "2025-09-02": [...] }
//

export async function saveRestHourEntries(
  crewId: number,
  vesselId: number,
  entriesByDate: Record<string, {slotIndex: number}[]>,
  workTypesByDate?: Record<string, (WorkType | null)[]>
  // remarksByDate?: Record<string, string | null>
): Promise<void> {
  try {
    for (const [entryDate, slots] of Object.entries(entriesByDate)) {
      // const commonRemarks = remarksByDate?.[entryDate] ?? null;

      const body = slots.map((s) => {
        const entryWorkType = workTypesByDate?.[entryDate]?.[s.slotIndex] ?? 'NORMAL'

        return {
          crewId,
          vesselId,
          entryDate,
          slotIndex: s.slotIndex,
          // keep the same field names your backend expects
          working: true,
          workType: entryWorkType,
          isApproved: false,
          // remarks: commonRemarks,
          createdBy: null,
        }
      })

      await axios.post(`${REST_HOURS_API_URL}/entries/bulk`, body, {
        params: {crewId, vesselId, entryDate},
      })
    }
  } catch (error: any) {
    console.error('❌ Error saving rest hours:', error)
    throw new Error(error.response?.data?.message || 'Error saving rest hours')
  }
}

// Compensatory rest APIs
export async function getCompRestRequirements(
  crewId: number,
  vesselId: number,
  status: CompRestStatus = 'OPEN'
): Promise<CompRestRequirement[]> {
  const {data} = await axios.get(`${REST_HOURS_API_URL}/comp-rest`, {
    params: {crewId, vesselId, status},
  })
  return data
}

export async function waiveCompRestRequirement(id: number, notes?: string) {
  const {data} = await axios.post(`${REST_HOURS_API_URL}/comp-rest/${id}/waive`, {notes})
  return data as CompRestRequirement
}

export async function satisfyCompRestRequirement(
  id: number,
  satisfiedMinutes?: number,
  notes?: string
) {
  const {data} = await axios.post(`${REST_HOURS_API_URL}/comp-rest/${id}/satisfy`, {
    satisfiedMinutes,
    notes,
  })
  return data as CompRestRequirement
}

// Mark slots back to REST (uncheck)
export async function deleteWorkingSlots(
  crewId: number,
  vesselId: number,
  entryDate: string, // 'YYYY-MM-DD'
  slotIndices: number[], // [0..47]
  recomputeForward = true
): Promise<any> {
  try {
    const {data} = await axios.put(`${REST_HOURS_API_URL}/entries/day/delete-slots`, {
      crewId,
      vesselId,
      entryDate,
      slotIndices,
      recomputeForward,
    })
    return data // DayRecomputeResponse (if your API returns one)
  } catch (error: any) {
    console.error('❌ Delete-slots failed:', error)
    throw new Error(error.response?.data?.message || `Delete-slots failed for ${entryDate}`)
  }
}

export async function getDailySummary(
  crewId: number,
  vesselId: number,
  date: string
): Promise<RestHourDailySummary> {
  try {
    const {data} = await axios.get(`${REST_HOURS_API_URL}/summaries`, {
      params: {crewId, vesselId, date},
    })
    return data
  } catch (error: any) {
    console.error('❌ Error fetching daily summary:', error)
    throw new Error(error.response?.data?.message || 'Failed fetching daily summary')
  }
}

export async function getRollingExceptionsCompany(
  crewId: number,
  vesselId: number,
  asOf?: string
): Promise<RollingExceptionResponse> {
  try {
    const {data} = await axios.get(`${REST_HOURS_API_URL}/entries/rolling-exceptions/company`, {
      params: {crewId, vesselId, asOf: asOf || undefined},
    })
    return data
  } catch (error: any) {
    console.error('❌ Error fetching rolling exceptions:', error)
    throw new Error(error.response?.data?.message || 'Failed fetching rolling exceptions')
  }
}

export async function getRollingMatrixSummaries(
  start: string,
  end: string,
  crewId?: number | null,
  vesselId?: number | null,
  regulation: Regulation = 'stcw',
  anchorMode?: string,
  anchorTime?: string,
  signal?: AbortSignal
): Promise<RollingMatrixResponse> {
  try {
    const {data} = await axios.get<RollingMatrixResponse>(
      `${REST_HOURS_API_URL}/summaries/rolling-range`,
      {
        signal,
        params: {
          start,
          end,
          crewId: crewId ?? undefined,
          vesselId: vesselId ?? undefined,
          regulation,
          readOnly: true,
          // anchor fields ignored by backend rolling engine; kept for compatibility
          anchorMode: undefined,
          anchorTime: undefined,
        },
      }
    )
    return data
  } catch (error: any) {
    if (axios.isCancel(error)) throw error
    console.error('❌ Error fetching rolling matrix summaries:', error)
    throw new Error(error.response?.data?.message || 'Failed fetching rolling matrix summaries')
  }
}

export interface RollingPreviewPayload {
  start: string
  end: string
  crewId: number
  vesselId: number
  regulation?: Regulation
  slots: PreviewSlotPayload[]
}

export async function previewRestHourViolations(
  payload: RollingPreviewPayload,
  signal?: AbortSignal
): Promise<RollingPreviewResponse> {
  try {
    const {data} = await axios.post<RollingPreviewResponse>(
      `${REST_HOURS_API_URL}/summaries/rolling-preview`,
      {...payload, anchorMode: undefined, anchorTime: undefined},
      {signal}
    )
    return data
  } catch (error: any) {
    console.error('❌ Error previewing rest hour violations:', error)
    throw new Error(error.response?.data?.message || 'Failed previewing rest hour violations')
  }
}

// Now returns array of summaries (same shape you used before)
export async function getRestHoursSummary(
  start: string,
  end: string,
  crewId?: number | null,
  vesselId?: number | null,
  regulation: Regulation = 'stcw'
): Promise<any[]> {
  try {
    const {data} = await axios.get(`${REST_SUMMARY_RANGE_API}/${regulation}`, {
      params: {
        start,
        end,
        crewId: crewId ?? undefined,
        vesselId: vesselId ?? undefined,
      },
    })
    return data?.summaries ?? []
  } catch (error: any) {
    console.error('❌ Error fetching summaries range:', error)
    throw new Error(error.response?.data?.message || 'Failed fetching summaries range')
  }
}

// If you also need rolling 7d totals alongside summaries
export async function getSummariesRange(
  start: string,
  end: string,
  crewId?: number | null,
  vesselId?: number | null,
  regulation: Regulation = 'stcw'
): Promise<{summaries: any[]; rolling: any[]}> {
  try {
    const {data} = await axios.get(`${REST_SUMMARY_RANGE_API}/${regulation}`, {
      params: {
        start,
        end,
        crewId: crewId ?? undefined,
        vesselId: vesselId ?? undefined,
      },
    })
    // return { summaries: data?.summaries ?? [], rolling7d: data?.rolling7d ?? [] };
    // server now returns stcwRolling / mlcRolling; normalize to .rolling for callers
    const rolling = regulation === 'stcw' ? data?.stcwRolling ?? [] : data?.mlcRolling ?? []
    return {summaries: data?.summaries ?? [], rolling}
  } catch (error: any) {
    console.error('❌ Error fetching summaries+rolling:', error)
    throw new Error(error.response?.data?.message || 'Failed fetching summaries range')
  }
}

// Approve/Unapprove a day summary
export async function approveRestDay(summaryId: number, approved: boolean): Promise<void> {
  try {
    const res = await axios.patch(
      `${REST_HOURS_API_URL}/entries/summaries/${summaryId}/approval`,
      null,
      {params: {approved}}
    )
    if (res.status !== 204) throw new Error(`Unexpected status ${res.status}`)
  } catch (error: any) {
    console.error('❌ Approval failed:', error)
    throw new Error(error.response?.data?.message || `Approval failed for summary ${summaryId}`)
  }
}

// NEW: Update remarks on a summary-day (preferred)
export async function patchSummaryRemarks(summaryId: number, remarks: string): Promise<void> {
  try {
    await axios.patch(
      `${REST_HOURS_API_URL}/summaries/${summaryId}/remarks`,
      {remarks},
      {headers: jsonHeaders}
    )
  } catch (error: any) {
    console.error('❌ PATCH summary remarks failed:', error)
    throw new Error(
      error.response?.data?.message || `Failed updating remarks for summary ${summaryId}`
    )
  }
}

export async function recomputeDailySummary(
  crewId: number,
  vesselId: number,
  date: string
): Promise<any> {
  try {
    const res = await axios.post(
      `${REST_HOURS_API_URL}/summaries/recompute`,
      null,
      {
        params: { crewId, vesselId, date },
        headers: jsonHeaders,
      }
    )
    return res.data
  } catch (error: any) {
    console.error('❌ Recompute daily summary failed:', error)
    throw error
  }
}

// export const saveRestHourEntries = async (
//   crewId: number,
//   vesselId: number,
//   entriesByDate: Record<string, RestHourEntry[]>
// ): Promise<void> => {
//   try {
//     // Loop through each date and call API separately
//     for (const [date, entries] of Object.entries(entriesByDate)) {
//       const url = `${REST_HOURS_API_URL}/entries/bulk?crewId=${crewId}&vesselId=${vesselId}&entryDate=${date}`

//       console.log('Saving entries for:', date, entries)

//       // Add isWorking:true to each entry object
//       const payload = entries.map(entry => ({
//         ...entry,
//         working: true
//       }))

//       const response = await axios.post(url, payload)

//       console.log(`✅ Saved for ${date}`, response.data)
//     }
//   } catch (error: any) {
//     console.error('❌ Error saving rest hours:', error)
//     throw new Error(error.response?.data?.message || 'Error saving rest hours')
//   }
// }

// export const getRestHourEntriesInRange = async (
//   crewId: number,
//   vesselId: number,
//   start: string,   // format: YYYY-MM-DD
//   end: string      // format: YYYY-MM-DD
// ): Promise<any[]> => {
//   try {
//     const url = `${REST_HOURS_API_URL}/rest-range?crewId=${crewId}&vesselId=${vesselId}&start=${start}&end=${end}`

//     const response = await axios.get<any[]>(url)

//     return response.data
//   } catch (error: any) {
//     console.error('❌ Error fetching rest hour entries:', error)
//     throw new Error(error.response?.data?.message || 'Error fetching rest hour entries')
//   }
// }
export const getRestHourEntriesInRange = async (
  crewId: number,
  vesselId: number,
  start: string,
  end: string
) => {
  try {
    const response = await axios.get(`${REST_HOURS_API_URL}/entries/rest-range`, {
      params: {
        crewId,
        vesselId,
        start,
        end,
      },
    })

    const rows = Array.isArray(response.data) ? response.data : []
    // normalize: prefer server `working`, fallback to `isWorking`, keep remarks
    return rows.map((e: any) => ({
      ...e,
      isWorking: e?.isWorking ?? e?.working ?? false,
      working: e?.working ?? e?.isWorking ?? false,
      isApproved: e?.isApproved ?? e?.approved ?? false,
      remarks: e?.remarks ?? null,
    }))
  } catch (error) {
    console.error('Error fetching rest range entries:', error)
    throw error
  }
}

// export const getRestHoursSummary = async (
//   start: string,
//   end: string
// ) => {
//   try {
//     const response = await axios.get(`${REST_HOURS_API_URL}/summaries/range`, {
//       params: {
//         start,
//         end
//       }
//     });

//     return response.data; // this should give you the array of entries
//   } catch (error) {
//     console.error("Error fetching rest range entries:", error);
//     throw error;
//   }
// };

//Application Form Crew Documents

export const getCrewDocument = async (crewId: number): Promise<CrewDocumentResponse | null> => {
  try {
    const res = await axios.get(`${API_URL}/crew/${crewId}/documents`)
    const data = res.data

    if (!data) return null

    if (Array.isArray(data)) {
      if (data.length === 0) return null
      return (
        data
          .filter((item) => item && typeof item.id === 'number')
          .sort((a, b) => (b.id ?? 0) - (a.id ?? 0))[0] ?? null
      )
    }

    return data
  } catch (e) {
    throw new Error('Failed to fetch crew document')
  }
}

export const createCrewDocument = async (
  crewId: number,
  documentData: CrewDocumentRequest
): Promise<CrewDocumentResponse> => {
  try {
    const response = await axios.post(`${API_URL}/crew/${crewId}/documents/create`, documentData)
    return response.data
  } catch (error) {
    throw new Error('Failed to create crew document')
  }
}

export const updateCrewDocument = async (
  crewId: number,
  documentId: number,
  documentData: CrewDocumentRequest
): Promise<CrewDocumentResponse> => {
  try {
    if (!documentId && documentId !== 0)
      throw new Error('updateCrewDocument called without a valid docId')
    const response = await axios.put(
      `${API_URL}/crew/${crewId}/documents/update/${documentId}`,
      documentData
    )
    return response.data
  } catch (error) {
    throw new Error('Failed to update crew document')
  }
}

export const patchCrewDocument = async (
  crewId: number,
  documentId: number,
  documentData: CrewDocumentRequest
): Promise<CrewDocumentResponse> => {
  try {
    if (!documentId && documentId !== 0)
      throw new Error('patchCrewDocument called without a valid docId')
    const response = await axios.patch(
      `${API_URL}/crew/${crewId}/documents/patch/${documentId}`,
      documentData
    )
    return response.data
  } catch (error) {
    throw new Error('Failed to patch crew document')
  }
}

// Academic Details APIs
export const getAcademicDetails = async (
  crewId: number
): Promise<AcademicQualificationResponse[]> => {
  try {
    const response = await axios.get(`${API_URL}/crew/${crewId}/academic-details`)
    return Array.isArray(response.data) ? response.data : [response.data]
  } catch (error: any) {
    console.error('Error fetching academic details:', error)
    throw new Error(error.response?.data?.message || 'Error fetching academic details')
  }
}

export const createAcademicDetails = async (
  crewId: number,
  academicData: AcademicQualificationRequest
): Promise<AcademicQualificationResponse> => {
  try {
    const response = await axios.post(`${API_URL}/crew/${crewId}/academic-details/create`, {
      qualification: academicData.qualification,
      institutionName: academicData.institutionName,
      boardOrUniversity: academicData.boardOrUniversity,
      dateOfPassing: academicData.dateOfPassing,
      gradeOrPercentage: academicData.gradeOrPercentage,
      englishProficiency: academicData.englishProficiency,
    })
    return response.data
  } catch (error: any) {
    console.error('Error creating academic details:', error)
    throw new Error(error.response?.data?.message || 'Error creating academic details')
  }
}

export const updateAcademicDetails = async (
  crewId: number,
  academicId: number,
  academicData: AcademicQualificationRequest
): Promise<AcademicQualificationResponse> => {
  try {
    if (!academicId && academicId !== 0)
      throw new Error('updateAcademicDetails called without a valid academicId')
    const response = await axios.put(
      `${API_URL}/crew/${crewId}/academic-details/update/${academicId}`,
      {
        qualification: academicData.qualification,
        institutionName: academicData.institutionName,
        boardOrUniversity: academicData.boardOrUniversity,
        dateOfPassing: academicData.dateOfPassing,
        gradeOrPercentage: academicData.gradeOrPercentage,
        englishProficiency: academicData.englishProficiency,
      }
    )
    return response.data
  } catch (error: any) {
    console.error('Error updating academic details:', error)
    throw new Error(error.response?.data?.message || 'Error updating academic details')
  }
}

// Watchkeeping Certificates APIs
export const getWatchkeepingCertificates = async (
  crewId: number
): Promise<WatchkeepingCertificateResponse[]> => {
  try {
    const response = await axios.get(`${API_URL}/crew/${crewId}/watchkeeping-certificates`)
    return Array.isArray(response.data) ? response.data : [response.data]
  } catch (error: any) {
    console.error('Error fetching watchkeeping certificates:', error)
    throw new Error(error.response?.data?.message || 'Error fetching watchkeeping certificates')
  }
}

export const createWatchkeepingCertificate = async (
  crewId: number,
  certificateData: WatchkeepingCertificateRequest
): Promise<WatchkeepingCertificateResponse> => {
  try {
    const response = await axios.post(
      `${API_URL}/crew/${crewId}/watchkeeping-certificates/create`,
      {
        certificateDetails: certificateData.certificateDetails,
        certificateNo: certificateData.certificateNo,
        dateOfIssue: certificateData.dateOfIssue,
        placeOfIssue: certificateData.placeOfIssue,
        validUntil: certificateData.validUntil,
      }
    )
    return response.data
  } catch (error: any) {
    console.error('Error creating watchkeeping certificate:', error)
    throw new Error(error.response?.data?.message || 'Error creating watchkeeping certificate')
  }
}

export const updateWatchkeepingCertificate = async (
  crewId: number,
  certificateId: number,
  certificateData: WatchkeepingCertificateRequest
): Promise<WatchkeepingCertificateResponse> => {
  try {
    if (!certificateId && certificateId !== 0)
      throw new Error('updateWatchkeepingCertificate called without a valid certificateId')
    const response = await axios.put(
      `${API_URL}/crew/${crewId}/watchkeeping-certificates/update/${certificateId}`,
      {
        certificateDetails: certificateData.certificateDetails,
        certificateNo: certificateData.certificateNo,
        dateOfIssue: certificateData.dateOfIssue,
        placeOfIssue: certificateData.placeOfIssue,
        validUntil: certificateData.validUntil,
      }
    )
    return response.data
  } catch (error: any) {
    console.error('Error updating watchkeeping certificate:', error)
    throw new Error(error.response?.data?.message || 'Error updating watchkeeping certificate')
  }
}

export const getNextOfKin = async (crewId: number): Promise<NextOfKinResponse | null> => {
  try {
    const response = await axios.get(`${API_URL}/crew/${crewId}/next-of-kin`)
    const data = response.data

    // API sometimes returns an array; pick the first/only record
    const kin = Array.isArray(data) ? data[0] ?? null : data ?? null

    // Optionally: if backend ever returns newest-first array and you want latest:
    // const doc = Array.isArray(data) ? (data.sort((a,b)=>b.id-a.id)[0] ?? null) : (data ?? null);

    return kin
  } catch (error: any) {
    console.error('Error fetching next of kin:', error)
    throw new Error(error.response?.data?.message || 'Error fetching next of kin')
  }
}

export const createNextOfKin = async (
  crewId: number,
  nextOfKinData: NextOfKinRequest
): Promise<NextOfKinResponse> => {
  try {
    const response = await axios.post(`${API_URL}/crew/${crewId}/next-of-kin/create`, {
      crewId: nextOfKinData.crewId,
      civilStatus: nextOfKinData.civilStatus,
      fullName: nextOfKinData.fullName,
      relationship: nextOfKinData.relationship,
      address: nextOfKinData.address,
      pinCode: nextOfKinData.pinCode,
      phoneStdCode: nextOfKinData.phoneStdCode,
      phoneNumber: nextOfKinData.phoneNumber,
    })
    return response.data
  } catch (error: any) {
    console.error('Error creating next of kin:', error)
    throw new Error(error.response?.data?.message || 'Error creating next of kin')
  }
}

export const updateNextOfKin = async (
  crewId: number,
  nextOfKinId: number,
  nextOfKinData: NextOfKinRequest
): Promise<NextOfKinResponse> => {
  try {
    if (!nextOfKinId && nextOfKinId !== 0)
      throw new Error('updateNextOfKin called without a valid nextOfKinId')
    const response = await axios.put(
      `${API_URL}/crew/${crewId}/next-of-kin/update/${nextOfKinId}`,
      {
        crewId: nextOfKinData.crewId,
        civilStatus: nextOfKinData.civilStatus,
        fullName: nextOfKinData.fullName,
        relationship: nextOfKinData.relationship,
        address: nextOfKinData.address,
        pinCode: nextOfKinData.pinCode,
        phoneStdCode: nextOfKinData.phoneStdCode,
        phoneNumber: nextOfKinData.phoneNumber,
      }
    )
    return response.data
  } catch (error: any) {
    console.error('Error updating next of kin:', error)
    throw new Error(error.response?.data?.message || 'Error updating next of kin')
  }
}

export const getBankingDetails = async (crewId: number): Promise<BankingDetailsResponse | null> => {
  try {
    const response = await axios.get(`${API_URL}/crew/${crewId}/banking-details`)
    const data = response.data
    if (Array.isArray(data)) {
      if (data.length === 0) return null
      // Keep Banking tab anchored to the first-created account.
      const primary = [...data].sort((a, b) => (a?.id ?? 0) - (b?.id ?? 0))[0]
      return {
        ...primary,
        salaryCurrency: primary?.salaryCurrency ?? primary?.accountCurrency ?? null,
        salaryOtherCurrency: primary?.salaryOtherCurrency ?? primary?.otherCurrency ?? null,
      }
    }
    if (!data) return null
    return {
      ...data,
      salaryCurrency: data?.salaryCurrency ?? data?.accountCurrency ?? null,
      salaryOtherCurrency: data?.salaryOtherCurrency ?? data?.otherCurrency ?? null,
    }
  } catch (error) {
    console.error('Error fetching banking details:', error)
    return null
  }
}

export const createBankingDetails = async (
  crewId: number,
  bankingData: BankingDetailsRequest
): Promise<BankingDetailsResponse> => {
  const response = await axios.post(`${API_URL}/crew/${crewId}/banking-details/create`, {
    crewId: bankingData.crewId,
    nameOfBeneficiary: bankingData.nameOfBeneficiary,
    accountNumber: bankingData.accountNumber,
    bankName: bankingData.bankName,
    branchName: bankingData.branchName,
    branchCode: bankingData.branchCode,
    ifscRtgsType: bankingData.ifscRtgsType,
    ifscRtgsCode: bankingData.ifscRtgsCode,
    swiftIandi: bankingData.swiftIandi,
    bankAddress: bankingData.bankAddress,
    accountCurrency: bankingData.salaryCurrency ?? bankingData.accountCurrency,
    otherCurrency: bankingData.salaryOtherCurrency ?? bankingData.otherCurrency,
    remarks: bankingData.remarks,
    basicWages: bankingData.basicWages,
    fixedOvertime109Hrs: bankingData.fixedOvertime109Hrs,
    leaveWages9DaysPerMonth: bankingData.leaveWages9DaysPerMonth,
    subSmb: bankingData.subSmb,
    tradeAllowance: bankingData.tradeAllowance,
    otherAllowance: bankingData.otherAllowance,
    ownersLoyaltyBonus: bankingData.ownersLoyaltyBonus,
    rejoiningBonus: bankingData.rejoiningBonus,
    overtimeRate: bankingData.overtimeRate,
    unionSocialDues: bankingData.unionSocialDues,
    cashAdvanceFromOffice: bankingData.cashAdvanceFromOffice,
    deductionBond: bankingData.deductionBond,
    deductionInternetCard: bankingData.deductionInternetCard,
    otherDeductions: bankingData.otherDeductions,
    deductionsTotal: bankingData.deductionsTotal,
    // Legacy fields retained for backward compatibility with old records.
    totalBasic: bankingData.totalBasic,
    leavePay: bankingData.leavePay,
    overtimePay: bankingData.overtimePay,
    incentive: bankingData.incentive,
    salary: bankingData.salary,
  })
  return {
    ...response.data,
    salaryCurrency: response.data?.salaryCurrency ?? response.data?.accountCurrency ?? null,
    salaryOtherCurrency: response.data?.salaryOtherCurrency ?? response.data?.otherCurrency ?? null,
  }
}

export const updateBankingDetails = async (
  crewId: number,
  bankingId: number,
  bankingData: BankingDetailsRequest
): Promise<BankingDetailsResponse> => {
  if (!bankingId && bankingId !== 0)
    throw new Error('updateBankingDetails called without a valid bankingId')
  const response = await axios.put(
    `${API_URL}/crew/${crewId}/banking-details/update/${bankingId}`,
    {
      crewId: bankingData.crewId,
      nameOfBeneficiary: bankingData.nameOfBeneficiary,
      accountNumber: bankingData.accountNumber,
      bankName: bankingData.bankName,
      branchName: bankingData.branchName,
      branchCode: bankingData.branchCode,
      ifscRtgsType: bankingData.ifscRtgsType,
      ifscRtgsCode: bankingData.ifscRtgsCode,
      swiftIandi: bankingData.swiftIandi,
      bankAddress: bankingData.bankAddress,
      accountCurrency: bankingData.salaryCurrency ?? bankingData.accountCurrency,
      otherCurrency: bankingData.salaryOtherCurrency ?? bankingData.otherCurrency,
      remarks: bankingData.remarks,
      basicWages: bankingData.basicWages,
      fixedOvertime109Hrs: bankingData.fixedOvertime109Hrs,
      leaveWages9DaysPerMonth: bankingData.leaveWages9DaysPerMonth,
      subSmb: bankingData.subSmb,
      tradeAllowance: bankingData.tradeAllowance,
      otherAllowance: bankingData.otherAllowance,
      ownersLoyaltyBonus: bankingData.ownersLoyaltyBonus,
      rejoiningBonus: bankingData.rejoiningBonus,
      overtimeRate: bankingData.overtimeRate,
      unionSocialDues: bankingData.unionSocialDues,
      cashAdvanceFromOffice: bankingData.cashAdvanceFromOffice,
      deductionBond: bankingData.deductionBond,
      deductionInternetCard: bankingData.deductionInternetCard,
      otherDeductions: bankingData.otherDeductions,
      deductionsTotal: bankingData.deductionsTotal,
      // Legacy fields retained for backward compatibility with old records.
      totalBasic: bankingData.totalBasic,
      leavePay: bankingData.leavePay,
      overtimePay: bankingData.overtimePay,
      incentive: bankingData.incentive,
      salary: bankingData.salary,
    }
  )
  return {
    ...response.data,
    salaryCurrency: response.data?.salaryCurrency ?? response.data?.accountCurrency ?? null,
    salaryOtherCurrency: response.data?.salaryOtherCurrency ?? response.data?.otherCurrency ?? null,
  }
}

export const getCourseCertificates = async (
  crewId: number
): Promise<CourseCertificateResponse[]> => {
  try {
    const response = await axios.get(`${API_URL}/crew-course-certificates/crew/${crewId}`)
    return Array.isArray(response.data) ? response.data : [response.data]
  } catch (error: any) {
    console.error('Error fetching course certificates:', error)
    throw new Error(error.response?.data?.message || 'Error fetching course certificates')
  }
}

export const createCourseCertificate = async (
  crewId: number,
  certificateData: CourseCertificate
): Promise<CourseCertificateResponse> => {
  try {
    const response = await axios.post(`${API_URL}/crew-course-certificates/create/${crewId}`, {
      certificateName: certificateData.certificateName,
      category: certificateData.category,
      certificateNumber: certificateData.certificateNumber || null,
      dateOfIssue: certificateData.dateOfIssue || null,
      dateOfExpiry: certificateData.dateOfExpiry || null,
      issuedBy: certificateData.issuedBy || null,
    })
    return response.data
  } catch (error: any) {
    console.error('Error creating course certificate:', error)
    throw new Error(error.response?.data?.message || 'Error creating course certificate')
  }
}

export const updateCourseCertificate = async (
  crewId: number,
  certificateId: number,
  certificateData: CourseCertificate
): Promise<CourseCertificateResponse> => {
  try {
    if (!certificateId && certificateId !== 0)
      throw new Error('updateCourseCertificate called without a valid certificateId')
    const response = await axios.put(
      `${API_URL}/crew-course-certificates/update/${certificateId}/${crewId}`,
      {
        certificateName: certificateData.certificateName,
        category: certificateData.category,
        certificateNumber: certificateData.certificateNumber || null,
        dateOfIssue: certificateData.dateOfIssue || null,
        dateOfExpiry: certificateData.dateOfExpiry || null,
        issuedBy: certificateData.issuedBy || null,
      }
    )
    return response.data
  } catch (error: any) {
    console.error('Error updating course certificate:', error)
    throw new Error(error.response?.data?.message || 'Error updating course certificate')
  }
}

export const deleteCourseCertificate = async (certificateId: number): Promise<void> => {
  try {
    await axios.delete(`${API_URL}/crew-course-certificates/delete/${certificateId}`)
  } catch (error: any) {
    console.error('Error deleting course certificate:', error)
    throw new Error(error.response?.data?.message || 'Error deleting course certificate')
  }
}

export const getSeaServices = async (crewId: number): Promise<SeaServiceResponse[]> => {
  try {
    const response = await axios.get(`${API_URL}/crew/${crewId}/sea-service`)
    return Array.isArray(response.data) ? response.data : [response.data]
  } catch (error: any) {
    console.error('Error fetching sea services:', error)
    throw new Error(error.response?.data?.message || 'Error fetching sea services')
  }
}

export const createSeaService = async (
  crewId: number,
  seaServiceData: SeaServiceRequest
): Promise<SeaServiceResponse> => {
  try {
    const response = await axios.post(`${API_URL}/crew/${crewId}/sea-service/create`, {
      serialNo: seaServiceData.serialNo,
      companyName: seaServiceData.companyName || null,
      vesselName: seaServiceData.vesselName || null,
      typeOfVesselFlag: seaServiceData.typeOfVesselFlag || null,
      grtdrt: seaServiceData.grtdrt || null,
      engineType: seaServiceData.engineType || null,
      kwtBhp: seaServiceData.kwtBhp || null,
      rank: seaServiceData.rank || null,
      fromDate: seaServiceData.fromDate || null,
      toDate: seaServiceData.toDate || null,
      totalMonthsDays: seaServiceData.totalMonthsDays || null,
      reasonForSignOff: seaServiceData.reasonForSignOff || null,
    })
    return response.data
  } catch (error: any) {
    console.error('Error creating sea service:', error)
    throw new Error(error.response?.data?.message || 'Error creating sea service')
  }
}

export const updateSeaService = async (
  crewId: number,
  seaServiceId: number,
  seaServiceData: SeaServiceRequest
): Promise<SeaServiceResponse> => {
  try {
    if (!seaServiceId && seaServiceId !== 0)
      throw new Error('updateSeaService called without a valid seaServiceId')
    const response = await axios.put(
      `${API_URL}/crew/${crewId}/sea-service/update/${seaServiceId}`,
      {
        serialNo: seaServiceData.serialNo,
        companyName: seaServiceData.companyName || null,
        vesselName: seaServiceData.vesselName || null,
        typeOfVesselFlag: seaServiceData.typeOfVesselFlag || null,
        grtdrt: seaServiceData.grtdrt || null,
        engineType: seaServiceData.engineType || null,
        kwtBhp: seaServiceData.kwtBhp || null,
        rank: seaServiceData.rank || null,
        fromDate: seaServiceData.fromDate || null,
        toDate: seaServiceData.toDate || null,
        totalMonthsDays: seaServiceData.totalMonthsDays || null,
        reasonForSignOff: seaServiceData.reasonForSignOff || null,
      }
    )
    return response.data
  } catch (error: any) {
    console.error('Error updating sea service:', error)
    throw new Error(error.response?.data?.message || 'Error updating sea service')
  }
}

export const deleteSeaService = async (crewId: number, seaServiceId: number): Promise<void> => {
  try {
    await axios.delete(`${API_URL}/crew/${crewId}/sea-service/delete/${seaServiceId}`)
  } catch (error: any) {
    console.error('Error deleting sea service:', error)
    throw new Error(error.response?.data?.message || 'Error deleting sea service')
  }
}

export const getAdditionalDetails = async (
  crewId: number
): Promise<AdditionalDetailsResponse | null> => {
  try {
    const response = await axios.get(`${API_URL}/crew/${crewId}/additional-details`)
    return response.data[0] || null
  } catch (error: any) {
    console.error('Error fetching additional details:', error)
    throw new Error(error.response?.data?.message || 'Error fetching additional details')
  }
}

export const createAdditionalDetails = async (
  crewId: number,
  additionalDetailsData: AdditionalDetailsRequest
): Promise<AdditionalDetailsResponse> => {
  try {
    const payload = Object.fromEntries(
      Object.entries(additionalDetailsData).filter(
        ([_, value]) => value !== null && value !== undefined
      )
    )
    const response = await axios.post(
      `${API_URL}/crew/${crewId}/additional-details/create`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )
    return response.data
  } catch (error: any) {
    console.error('Error creating additional details:', error)
    throw new Error(error.response?.data?.message || 'Error creating additional details')
  }
}

export const updateAdditionalDetails = async (
  crewId: number,
  additionalDetailsId: number,
  additionalDetailsData: AdditionalDetailsRequest
): Promise<AdditionalDetailsResponse> => {
  try {
    if (!additionalDetailsId && additionalDetailsId !== 0)
      throw new Error('updateAdditionalDetails called without a valid additionalDetailsId')
    const payload = Object.fromEntries(
      Object.entries(additionalDetailsData).filter(
        ([_, value]) => value !== null && value !== undefined
      )
    )
    const response = await axios.put(
      `${API_URL}/crew/${crewId}/additional-details/update/${additionalDetailsId}`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )
    return response.data
  } catch (error: any) {
    console.error('Error updating additional details:', error)
    throw new Error(error.response?.data?.message || 'Error updating additional details')
  }
}

export const getSignedOnCrewList = async (): Promise<Crew[]> => {
  try {
    const response = await axios.get(SIGNEDON_CREW_API_URL)
    console.log('signed on crew:', response.data)

    return response.data
  } catch (error: any) {
    console.error('Error fetching crew list:', error)
    throw new Error(error.response?.data?.message || 'Error fetching Crew list')
  }
}

// ================= FLAG DOCUMENTS =================

export const getFlagDocuments = async (crewId: number): Promise<CrewFlagDocument[]> => {
  const res = await axios.get(`${API_URL}/crew/${crewId}/flag-documents`)
  return res.data
}

export interface SaveFlagDocumentPayload {
  crewId: number
  flag: FlagCountry
  docType: FlagDocType
  documentNumber?: string
  issueDate?: string // yyyy-MM-dd
  expiryDate?: string
  file?: File | null
}

export const getOnboardingToken = (): string | null => {
  if (typeof window === 'undefined') return null
  const params = new URLSearchParams(window.location.search)
  return params.get('token')
}

export const saveFlagDocument = async (
  payload: SaveFlagDocumentPayload
): Promise<CrewFlagDocument> => {
  const {crewId, flag, docType, documentNumber, issueDate, expiryDate, file} = payload
  const fd = new FormData()
  fd.append('flag', flag)
  fd.append('docType', docType)
  if (documentNumber) fd.append('documentNumber', documentNumber)
  if (issueDate) fd.append('issueDate', issueDate)
  if (expiryDate) fd.append('expiryDate', expiryDate)
  if (file) fd.append('file', file)

  const token = getOnboardingToken()
  if (token) {
    const res = await axios.post(`${API_URL}/crew/onboard/flag-document`, fd, {
      params: {token},
    })
    return res.data
  }

  const res = await axios.post(`${API_URL}/crew/${crewId}/flag-documents`, fd)
  return res.data
}

export const deleteFlagDocument = async (crewId: number, id: number): Promise<void> => {
  const token = getOnboardingToken()
  if (token) {
    await axios.delete(`${API_URL}/crew/onboard/flag-document/${id}`, {
      params: {token},
    })
    return
  }
  await axios.delete(`${API_URL}/crew/${crewId}/flag-documents/${id}`)
}

export const deleteFlagDocumentFile = async (crewId: number | null, id: number): Promise<void> => {
  const token = getOnboardingToken()
  if (token) {
    await axios.delete(`${API_URL}/crew/onboard/flag-document/${id}/file`, {params: {token}})
    return
  }
  await axios.delete(`${API_URL}/crew/${crewId}/flag-documents/${id}/file`)
}

export const fetchFlagDocumentBlob = async (crewId: number, id: number): Promise<Blob> => {
  const token = getOnboardingToken()
  if (token) {
    const res = await axios.get(`${API_URL}/crew/onboard/flag-document/${id}/view`, {
      params: {token},
      responseType: 'blob',
    })
    return res.data
  }
  const res = await axios.get(`${API_URL}/crew/${crewId}/flag-documents/${id}/view`, {
    responseType: 'blob',
  })
  return res.data
}

// Watchkeeping certificate file upload
export async function uploadWatchkeepingFile(
  crewId: number,
  certificateId: number,
  file: File
): Promise<void> {
  const formData = new FormData()
  formData.append('file', file)

  const token = getOnboardingToken()
  if (token) {
    await axios.post(`${API_URL}/crew/onboard/watchkeeping/${certificateId}/file`, formData, {
      headers: {'Content-Type': 'multipart/form-data'},
      params: {token},
    })
    return
  }

  await axios.post(
    `${API_URL}/crew/${crewId}/watchkeeping-certificates/${certificateId}/file`,
    formData,
    {
      headers: {'Content-Type': 'multipart/form-data'},
    }
  )
}

// View file
export async function fetchWatchkeepingFileBlob(
  crewId: number,
  certificateId: number
): Promise<Blob> {
  const token = getOnboardingToken()
  if (token) {
    const res = await axios.get(`${API_URL}/crew/onboard/watchkeeping/${certificateId}/view`, {
      params: {token},
      responseType: 'blob',
    })
    return res.data
  }
  const res = await axios.get(
    `${API_URL}/crew/${crewId}/watchkeeping-certificates/${certificateId}/view`,
    {responseType: 'blob'}
  )
  return res.data
}

// Delete file
export async function deleteWatchkeepingFile(crewId: number, certificateId: number): Promise<void> {
  const token = getOnboardingToken()
  if (token) {
    await axios.delete(`${API_URL}/crew/onboard/watchkeeping/${certificateId}/file`, {
      params: {token},
    })
    return
  }
  await axios.delete(`${API_URL}/crew/${crewId}/watchkeeping-certificates/${certificateId}/file`)
}

// Course certificate file upload
export async function uploadCourseCertificateFile(
  certificateId: number,
  file: File
): Promise<void> {
  const formData = new FormData()
  formData.append('file', file)

  const token = getOnboardingToken()
  if (token) {
    await axios.post(`${API_URL}/crew/onboard/course/${certificateId}/file`, formData, {
      headers: {'Content-Type': 'multipart/form-data'},
      params: {token},
    })
    return
  }

  await axios.post(`${API_URL}/crew-course-certificates/${certificateId}/file`, formData, {
    headers: {'Content-Type': 'multipart/form-data'},
  })
}

export async function fetchCourseCertificateFileBlob(certificateId: number): Promise<Blob> {
  const token = getOnboardingToken()
  if (token) {
    const res = await axios.get(`${API_URL}/crew/onboard/course/${certificateId}/view`, {
      params: {token},
      responseType: 'blob',
    })
    return res.data
  }
  const res = await axios.get(`${API_URL}/crew-course-certificates/${certificateId}/view`, {
    responseType: 'blob',
  })
  return res.data
}

export async function deleteCourseCertificateFile(certificateId: number): Promise<void> {
  const token = getOnboardingToken()
  if (token) {
    await axios.delete(`${API_URL}/crew/onboard/course/${certificateId}/file`, {params: {token}})
    return
  }
  await axios.delete(`${API_URL}/crew-course-certificates/${certificateId}/file`)
}

// ──────────────── Crew Onboarding APIs ────────────────

export interface OnboardingInitiateResponse {
  message: string
  crewId: number
  token: string
  onboardingLink: string
  username?: string
  password?: string
  emailSent?: boolean
}

export interface OnboardingVerifyResponse {
  valid: boolean
  message: string
  crewId?: number
  crewName?: string
  crewEmail?: string
}

export interface OnboardingCompleteResponse {
  success: boolean
  message: string
  crewId?: number
}

export interface OnboardingDocumentDraftResponse {
  success: boolean
  message?: string
  data?: CrewDocumentResponse | null
}

/**
 * Step 1: Initiate crew onboarding from the admin side.
 * Sends an email to the crew member with a public link.
 */
export const initiateCrewOnboarding = async (
  payload: Record<string, any>
): Promise<OnboardingInitiateResponse> => {
  try {
    const response = await axios.post(`${CREW_API_URL}/onboard/initiate`, payload)
    return response.data
  } catch (error: any) {
    console.error('Error initiating crew onboarding:', error)
    throw new Error(error.response?.data?.message || 'Error initiating crew onboarding')
  }
}

/**
 * Step 2: Verify onboarding token (public, no auth required).
 */
export const verifyCrewOnboardingToken = async (
  token: string
): Promise<OnboardingVerifyResponse> => {
  try {
    const response = await axios.get(`${CREW_API_URL}/onboard/verify`, {
      params: {token},
    })
    return response.data
  } catch (error: any) {
    console.error('Error verifying onboarding token:', error)
    throw new Error(error.response?.data?.message || 'Error verifying onboarding token')
  }
}

/**
 * Save draft: intermediate save for onboarding (public, no auth required).
 * Does NOT revoke the token — crew can come back later.
 */
export const saveCrewOnboardingDraft = async (
  token: string,
  crewDto: Record<string, any>
): Promise<OnboardingCompleteResponse> => {
  try {
    const response = await axios.post(`${CREW_API_URL}/onboard/save-draft`, crewDto, {
      params: {token},
    })
    return response.data
  } catch (error: any) {
    console.error('Error saving crew onboarding draft:', error)
    throw new Error(error.response?.data?.message || 'Error saving crew onboarding draft')
  }
}

/**
 * Get document draft data for the onboarding crew.
 */
export const getCrewOnboardingDocumentDraft = async (
  token: string
): Promise<OnboardingDocumentDraftResponse> => {
  try {
    const response = await axios.get(`${CREW_API_URL}/onboard/document-draft`, {
      params: {token},
    })
    return response.data
  } catch (error: any) {
    console.error('Error fetching document draft:', error)
    throw new Error(error.response?.data?.message || 'Error fetching document draft')
  }
}

/**
 * Save document draft data for the onboarding crew.
 */
export const saveCrewOnboardingDocumentDraft = async (
  token: string,
  data: Record<string, any>
): Promise<OnboardingDocumentDraftResponse> => {
  try {
    const response = await axios.post(`${CREW_API_URL}/onboard/save-document-draft`, data, {
      params: {token},
    })
    return response.data
  } catch (error: any) {
    console.error('Error saving document draft:', error)
    throw new Error(error.response?.data?.message || 'Error saving document draft')
  }
}

/**
 * Step 3: Complete crew onboarding (public, no auth required).
 * Crew member fills in remaining details.
 */
export const completeCrewOnboarding = async (
  token: string,
  crewDto: Record<string, any>
): Promise<OnboardingCompleteResponse> => {
  try {
    const response = await axios.post(`${CREW_API_URL}/onboard/complete`, crewDto, {
      params: {token},
    })
    return response.data
  } catch (error: any) {
    console.error('Error completing crew onboarding:', error)
    throw new Error(error.response?.data?.message || 'Error completing crew onboarding')
  }
}

/**
 * Fetch full onboarding draft.
 */
export const getCrewOnboardingDraft = async (token: string): Promise<any> => {
  try {
    const response = await axios.get(`${CREW_API_URL}/onboard/get-draft`, {
      params: {token},
    })
    return response.data
  } catch (error: any) {
    console.error('Error fetching onboarding draft:', error)
    throw new Error(error.response?.data?.message || 'Error fetching onboarding draft')
  }
}

/**
 * Save Academic Draft.
 */
export const saveCrewOnboardingAcademic = async (token: string, dtos: any[]): Promise<any> => {
  try {
    const response = await axios.post(`${CREW_API_URL}/onboard/academic`, dtos, {
      params: {token},
    })
    return response.data
  } catch (error: any) {
    console.error('Error saving academic draft:', error)
    throw new Error(error.response?.data?.message || 'Error saving academic draft')
  }
}

/**
 * Save Watchkeeping Draft.
 */
export const saveCrewOnboardingWatchkeeping = async (token: string, dtos: any[]): Promise<any> => {
  try {
    const response = await axios.post(`${CREW_API_URL}/onboard/watchkeeping`, dtos, {
      params: {token},
    })
    return response.data
  } catch (error: any) {
    console.error('Error saving watchkeeping draft:', error)
    throw new Error(error.response?.data?.message || 'Error saving watchkeeping draft')
  }
}

/**
 * Save Next of Kin Draft.
 */
export const saveCrewOnboardingKin = async (token: string, dto: any): Promise<any> => {
  try {
    const response = await axios.post(`${CREW_API_URL}/onboard/kin`, dto, {
      params: {token},
    })
    return response.data
  } catch (error: any) {
    console.error('Error saving next of kin draft:', error)
    throw new Error(error.response?.data?.message || 'Error saving next of kin draft')
  }
}

/**
 * Save Banking Draft.
 */
export const saveCrewOnboardingBanking = async (token: string, dto: any): Promise<any> => {
  try {
    const response = await axios.post(`${CREW_API_URL}/onboard/banking`, dto, {
      params: {token},
    })
    return response.data
  } catch (error: any) {
    console.error('Error saving banking draft:', error)
    throw new Error(error.response?.data?.message || 'Error saving banking draft')
  }
}

/**
 * Save Courses Draft.
 */
export const saveCrewOnboardingCourses = async (token: string, dtos: any[]): Promise<any> => {
  try {
    const response = await axios.post(`${CREW_API_URL}/onboard/courses`, dtos, {
      params: {token},
    })
    return response.data
  } catch (error: any) {
    console.error('Error saving courses draft:', error)
    throw new Error(error.response?.data?.message || 'Error saving courses draft')
  }
}

/**
 * Save Sea Service Draft.
 */
export const saveCrewOnboardingSeaService = async (token: string, dtos: any[]): Promise<any> => {
  try {
    const response = await axios.post(`${CREW_API_URL}/onboard/sea-service`, dtos, {
      params: {token},
    })
    return response.data
  } catch (error: any) {
    console.error('Error saving sea service draft:', error)
    throw new Error(error.response?.data?.message || 'Error saving sea service draft')
  }
}

/**
 * Save Other Details Draft.
 */
export const saveCrewOnboardingOther = async (
  token: string,
  additionalDetails: any,
  references: any[]
): Promise<any> => {
  try {
    const response = await axios.post(
      `${CREW_API_URL}/onboard/other`,
      {additionalDetails, references},
      {params: {token}}
    )
    return response.data
  } catch (error: any) {
    console.error('Error saving other details draft:', error)
    throw new Error(error.response?.data?.message || 'Error saving other details draft')
  }
}

export const checkCrewNtbe = async (params: {
  cdcNumber?: string
  crewId?: number
  companyGroupAdminId?: number
}): Promise<{ isNtbe: boolean; message: string }> => {
  try {
    const response = await axios.get(`${API_URL}/crew/check-ntbe`, { params })
    return response.data
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Unable to verify NTBE status. Please try again.')
  }
}
